import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import includes from "lodash/includes";
import React, { useEffect, useState } from "react";
import BSInput from "../../../common/js/components/BSInput";
import { getSpecialtyTypeDisplayNameNoPropertyType } from "../property/constants";
import { decimalToPercent, round, toSqftWithoutAppend } from "../utils/operations";
import operations from "./ducks/operations";

const StyledTableRow = styled(TableRow)({
    "&:nth-of-type(odd)": {
        backgroundColor: "#e7e6e6",
    },
    height: "50%",
});

const StyledTableCell = styled(TableCell)({
    whiteSpace: "nowrap",
});

const StyledCollapseContainer = styled(TableCell)({
    paddingBottom: 0,
    paddingTop: 0,
    whiteSpace: "nowrap",
    border: "none",
    "&:nth-of-type(odd)": {
        backgroundColor: "#e7e6e6",
    },
    height: "50%",
});

const StyledNotesContainer = styled("ul")({
    listStyleType: "circle",
    marginLeft: "5em",
    display: "list-item",
});

const StyledNoteEditMode = styled(BSInput)({
    borderColor: "#A0A6A0",
});

const StyledSaveIcon = styled(SaveIcon)({
    fontSize: "smaller",
});

const StyledCancelIcon = styled(CancelIcon)({
    fontSize: "smaller",
});

const ResearchSnapshotRow = (props) => {
    const { snapshot, columns, role, editResponse } = props;
    const [initialSnapshot, setInitialSnapshot] = useState({});
    const [newSnapshot, setNewSnapshot] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [disableCancel, setDisableCancel] = useState(false);
    const [disableSave, setDisableSave] = useState(false);
    const [open, setOpen] = useState(false);

    const cancelEditState = () => {
        setEditMode(false);
        setNewSnapshot(initialSnapshot);
    };
    const enterEditState = () => {
        setEditMode(true);
    };
    const exitEditState = () => {
        setEditMode(false);
    };
    const saveChanges = async () => {
        setDisableCancel(true);
        setDisableSave(true);
        const response = await operations.editExistingSnapshot(snapshot.researchSnapshotKey, newSnapshot);
        if (response.status >= 200 && response.status < 300) {
            editResponse(true);
            setInitialSnapshot(response.data);
            exitEditState();
        } else {
            editResponse(false);
        }
        setDisableCancel(false);
        setDisableSave(false);
    };
    const onChange = (e) => {
        // React funny business
        // https://stackoverflow.com/questions/44708518/event-target-is-null-inside-functional-setstate
        const { id, value } = e.target;
        setNewSnapshot((prevState) => ({ ...prevState, [id]: value }));
    };
    const cellValidation = (value, group) => {
        switch (group) {
            case "specialtyType":
                return getSpecialtyTypeDisplayNameNoPropertyType(value);
            case "lease":
                return value !== null ? round(value, 2) : "";
            case "percentage":
                return decimalToPercent(value, true);
            case "sqFt":
                return toSqftWithoutAppend(value);
            default:
                return value;
        }
    };

    useEffect(() => {
        setInitialSnapshot(snapshot);
        setNewSnapshot(snapshot);
    }, []);

    return (
        <React.Fragment>
            <StyledTableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                {columns.map((col) => {
                    const { group, field, editable } = col;
                    switch (group) {
                        case "notes":
                            return (
                                <StyledTableCell align="center">
                                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                </StyledTableCell>
                            );
                        case "edit":
                            return (
                                <StyledTableCell align="center">
                                    {editMode ? (
                                        <div>
                                            <IconButton
                                                disabled={disableSave}
                                                aria-label="save edit state"
                                                onClick={saveChanges}
                                            >
                                                <StyledSaveIcon />
                                            </IconButton>
                                            <IconButton
                                                disabled={disableCancel}
                                                aria-label="cancel edit state"
                                                onClick={cancelEditState}
                                            >
                                                <StyledCancelIcon />
                                            </IconButton>
                                        </div>
                                    ) : (
                                        <IconButton aria-label="enter exit state" size="small" onClick={enterEditState}>
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </StyledTableCell>
                            );

                        default:
                            const value = cellValidation(newSnapshot[field], group);
                            const cellValue =
                                value === null ||
                                value === undefined ||
                                (typeof value === "string" && value.length === 0)
                                    ? "-"
                                    : value;
                            return (
                                <StyledTableCell align="center">
                                    {editMode &&
                                    editable &&
                                    includes(["research", "researchAdmin", "dataAdmin"], role) ? (
                                        <BSInput
                                            id={field}
                                            value={newSnapshot[field]}
                                            onChange={onChange}
                                            type={group === "sqFt" ? "number" : null}
                                            endAdornment={group === "percentage" ? "%" : null}
                                            extraInputStyles={{ borderColor: "#A0A6A0" }}
                                            noFullWidth={true}
                                        />
                                    ) : (
                                        cellValue
                                    )}
                                </StyledTableCell>
                            );
                    }
                })}
            </StyledTableRow>
            <StyledCollapseContainer colSpan={50}>
                <Collapse in={open} timeout="auto">
                    {editMode ? (
                        <StyledNoteEditMode id={"notes"} value={newSnapshot["notes"]} onChange={onChange} />
                    ) : (
                        <StyledNotesContainer>{newSnapshot["notes"]}</StyledNotesContainer>
                    )}
                </Collapse>
            </StyledCollapseContainer>
        </React.Fragment>
    );
};

export default ResearchSnapshotRow;
